import { buildStoryblokUrl } from "~/utils/storyblok/buildStoryblokUrl";

interface StoryblokApiLike {
	get(
		url: string,
		params?: Record<string, unknown>
	): Promise<{ data: { story: unknown } }>;
}

interface FetchOptions {
	version: string;
	language: string;
	resolve_links?: "url" | "story";
}

interface FolderConfig {
	/**
	 * Ordered list of folder prefixes to try. First non-empty, unique
	 * entry is attempted first; subsequent entries are second-chance
	 * attempts. Empty / null / duplicate entries are skipped, so the
	 * caller can pass raw `useStoryblokSiteFolder` outputs without
	 * pre-filtering.
	 */
	folders?: (string | null | undefined)[];
	/**
	 * When true, the resolver does not fall back to a root-level request
	 * (no folder prefix). Enable in multi-site deploys to prevent
	 * `/<folder>/<slug>` URLs from resolving via the root fallback,
	 * which would create duplicate content.
	 * Default false — preserves legacy single-folder behaviour.
	 */
	strict?: boolean;
}

export async function fetchStoryblokContentByFolder(
	api: StoryblokApiLike,
	pathOrPaths: string | string[],
	options: FetchOptions,
	folderConfig: FolderConfig
): Promise<{ data: { story: unknown } }> {
	const { folders = [], strict } = folderConfig;
	// When an array is passed:
	//   - paths[0]    → "primary" — tried across the full folder chain
	//   - paths[1...] → legacy/migration root-only paths — tried at the
	//                   space root only, after the primary chain exhausts.
	// Example: `["layouts/main", "layouts"]` for a multi-site deploy →
	//   site-uk/layouts/main → site-main/layouts/main → layouts/main → layouts
	const paths = Array.isArray(pathOrPaths) ? pathOrPaths : [pathOrPaths];
	const primaryPath = paths[0] ?? "";
	const rootOnlyFallbacks = paths.slice(1);

	const foldersToTry: (string | null)[] = [];
	const addFolderAttempt = (folder?: string | null) => {
		if (!folder || foldersToTry.includes(folder)) {
			return;
		}
		foldersToTry.push(folder);
	};

	folders.forEach(addFolderAttempt);
	if (!strict) {
		foldersToTry.push(null);
	}

	let lastError: unknown;
	const attempted: string[] = [];

	for (const folder of foldersToTry) {
		try {
			const url = buildStoryblokUrl(primaryPath, folder);
			attempted.push(folder ? `${folder}/${primaryPath}` : primaryPath);
			const { data } = await api.get(url, options);
			if (data?.story) return { data };
		} catch (e) {
			lastError = e;
		}
	}

	for (const path of rootOnlyFallbacks) {
		try {
			const url = buildStoryblokUrl(path, null);
			attempted.push(path);
			const { data } = await api.get(url, options);
			if (data?.story) return { data };
		} catch (e) {
			lastError = e;
		}
	}

	const attemptedSummary =
		attempted.length > 0 ? attempted.join(", ") : "(none — empty folder chain)";
	const message = `Failed to fetch Storyblok story "${primaryPath}". Tried: ${attemptedSummary}`;
	if (lastError instanceof Error) {
		throw new Error(`${message}. Last error: ${lastError.message}`, {
			cause: lastError
		});
	}
	throw new Error(message);
}
